/**
 * Advanced Geofencing Utilities for Atlas Device Tracking
 * Provides fast, efficient geofencing calculations for real-time notifications
 */

export const DEFAULT_ORGANIZATION_ID = 'drishti_lifesaving_org_id';

/**
 * Whether the user may view beach polygon geofences on the map.
 */
export const canViewBeachGeofences = (permissions) => {
  if (!permissions || typeof permissions !== 'object') return false;
  if (permissions.viewGeofences === true) return true;
  if (Array.isArray(permissions.viewGeofences)) {
    if (permissions.viewGeofences.length === 0) return false;
    return permissions.viewGeofences.some(
      (t) => t === 'beach' || t === 'all' || t === true
    );
  }
  return false;
};

/**
 * Normalize Firestore polygon vertices to { lat, lng }[] for map rendering.
 */
export const normalizePolygonPoints = (rawPolygon) => {
  if (!Array.isArray(rawPolygon) || rawPolygon.length < 3) return [];

  const out = [];
  for (const p of rawPolygon) {
    if (p == null) continue;
    let lat;
    let lng;
    if (typeof p.lat === 'number' && typeof p.lng === 'number') {
      lat = p.lat;
      lng = p.lng;
    } else if (typeof p.latitude === 'number' && typeof p.longitude === 'number') {
      lat = p.latitude;
      lng = p.longitude;
    } else if (Array.isArray(p) && p.length >= 2) {
      lat = Number(p[0]);
      lng = Number(p[1]);
    } else {
      continue;
    }
    if (Number.isNaN(lat) || Number.isNaN(lng)) continue;
    out.push({ lat, lng });
  }
  return out.length >= 3 ? out : [];
};

/**
 * Map a Firestore geofences document to a beach location object for Redux/map.
 */
export const mapGeofenceDocToBeach = (docSnap, orgId) => {
  const data = docSnap.data() || {};
  const polygon = normalizePolygonPoints(data.polygon);
  return {
    id: docSnap.id,
    name: data.name || 'Unknown Beach',
    polygon,
    render_type: 'polygon',
    organizationId: data.organizationId || orgId,
    Vehicle_data: data.Vehicle_data || {},
  };
};

/**
 * Calculate distance between two points using Haversine formula
 * @param {Object} point1 - {lat, lng}
 * @param {Object} point2 - {lat, lng}
 * @returns {number} Distance in meters
 */
export const calculateDistance = (point1, point2) => {
  const R = 6371000; // Earth's radius in meters
  const dLat = toRadians(point2.lat - point1.lat);
  const dLng = toRadians(point2.lng - point1.lng);
  
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(point1.lat)) * Math.cos(toRadians(point2.lat)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Convert degrees to radians
 * @param {number} degrees
 * @returns {number} radians
 */
const toRadians = (degrees) => {
  return degrees * (Math.PI / 180);
};

/**
 * Check if a point is inside a circular geofence
 * @param {Object} point - {lat, lng}
 * @param {Object} geofence - {center: {lat, lng}, radius: number}
 * @returns {boolean}
 */
export const isPointInCircle = (point, geofence) => {
  if (!geofence.center || !geofence.radius) return false;
  
  const distance = calculateDistance(point, geofence.center);
  return distance <= geofence.radius;
};

/**
 * Check if a point is inside a polygon using ray casting algorithm
 * @param {Object} point - {lat, lng}
 * @param {Array} polygon - Array of {lat, lng} points
 * @returns {boolean}
 */
export const isPointInPolygon = (point, polygon) => {
  if (!polygon || polygon.length < 3) return false;
  
  let inside = false;
  const { lat, lng } = point;
  
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const { lat: latI, lng: lngI } = polygon[i];
    const { lat: latJ, lng: lngJ } = polygon[j];
    
    if (((lngI > lng) !== (lngJ > lng)) &&
        (lat < (latJ - latI) * (lng - lngI) / (lngJ - lngI) + latI)) {
      inside = !inside;
    }
  }
  
  return inside;
};

/**
 * Check if a point is inside any geofence (circle or polygon)
 * @param {Object} point - {lat, lng}
 * @param {Object} geofence - Geofence object with type and coordinates
 * @returns {boolean}
 */
export const isPointInGeofence = (point, geofence) => {
  if (!point || !geofence) return false;
  
  // Handle circular geofences (towers)
  if (geofence.type === 'circle' || geofence.center) {
    return isPointInCircle(point, geofence);
  }
  
  // Handle polygon geofences (beaches, custom areas)
  if (geofence.type === 'polygon' || geofence.polygon || geofence.coordinates) {
    const polygon = geofence.polygon || geofence.coordinates;
    return isPointInPolygon(point, polygon);
  }
  
  return false;
};

/**
 * Get all geofences that contain a point
 * @param {Object} point - {lat, lng}
 * @param {Array} geofences - Array of geofence objects
 * @returns {Array} Array of geofence IDs that contain the point
 */
export const getContainingGeofences = (point, geofences) => {
  if (!point || !geofences || !Array.isArray(geofences)) return [];
  
  return geofences
    .filter(geofence => isPointInGeofence(point, geofence))
    .map(geofence => geofence.id || geofence.name);
};

/**
 * Check if device has moved between geofences
 * @param {Object} oldPoint - Previous location {lat, lng}
 * @param {Object} newPoint - Current location {lat, lng}
 * @param {Array} geofences - Array of geofence objects
 * @returns {Object} {entered: [], exited: [], stayed: []}
 */
export const checkGeofenceTransitions = (oldPoint, newPoint, geofences) => {
  if (!oldPoint || !newPoint || !geofences) {
    return { entered: [], exited: [], stayed: [] };
  }
  
  const oldGeofences = getContainingGeofences(oldPoint, geofences);
  const newGeofences = getContainingGeofences(newPoint, geofences);
  
  const entered = newGeofences.filter(id => !oldGeofences.includes(id));
  const exited = oldGeofences.filter(id => !newGeofences.includes(id));
  const stayed = oldGeofences.filter(id => newGeofences.includes(id));
  
  return { entered, exited, stayed };
};

/**
 * Normalize device coordinates to consistent format
 * @param {Object} device - Device object
 * @returns {Object|null} Normalized coordinates {lat, lng} or null
 */
export const normalizeDeviceCoordinates = (device) => {
  if (!device) return null;
  
  // Try different coordinate field names
  const coordFields = ['location', 'coordinates', 'position', 'latlng'];
  
  for (const field of coordFields) {
    if (device[field]) {
      const coords = device[field];
      
      // Handle GeoPoint format
      if (coords.latitude !== undefined && coords.longitude !== undefined) {
        return { lat: coords.latitude, lng: coords.longitude };
      }
      
      // Handle array format [lat, lng]
      if (Array.isArray(coords) && coords.length >= 2) {
        return { lat: coords[0], lng: coords[1] };
      }
      
      // Handle object format {lat, lng}
      if (coords.lat !== undefined && coords.lng !== undefined) {
        return { lat: coords.lat, lng: coords.lng };
      }
    }
  }
  
  return null;
};

/**
 * Check if coordinates are valid
 * @param {Object} coords - {lat, lng}
 * @returns {boolean}
 */
export const isValidCoordinates = (coords) => {
  if (!coords || typeof coords.lat !== 'number' || typeof coords.lng !== 'number') {
    return false;
  }
  
  return coords.lat >= -90 && coords.lat <= 90 && 
         coords.lng >= -180 && coords.lng <= 180 &&
         !isNaN(coords.lat) && !isNaN(coords.lng);
};

/**
 * Create a geofence object from different input formats
 * @param {Object} input - Geofence data
 * @returns {Object} Normalized geofence object
 */
export const normalizeGeofence = (input) => {
  if (!input) return null;
  
  const geofence = {
    id: input.id || input.name || `geofence_${Date.now()}`,
    name: input.name || input.id || 'Unknown Geofence',
    type: input.type || 'polygon'
  };
  
  // Handle circular geofences (towers)
  if (input.center || (input.location && input.radius)) {
    geofence.type = 'circle';
    geofence.center = input.center || {
      lat: input.location?.latitude || input.location?.lat,
      lng: input.location?.longitude || input.location?.lng
    };
    geofence.radius = input.radius || 300;
  }
  
  // Handle polygon geofences
  if (input.polygon || input.coordinates) {
    geofence.type = 'polygon';
    geofence.polygon = input.polygon || input.coordinates;
  }
  
  return geofence;
};

/**
 * Batch process multiple devices for geofencing
 * @param {Array} devices - Array of device objects
 * @param {Array} geofences - Array of geofence objects
 * @param {Object} previousStates - Previous device states {deviceId: {geofences: []}}
 * @returns {Object} Geofence transitions for all devices
 */
export const batchProcessGeofencing = (devices, geofences, previousStates = {}) => {
  const results = {};
  
  devices.forEach(device => {
    const deviceId = device.id || device.device_id || device.name;
    const currentCoords = normalizeDeviceCoordinates(device);
    
    if (!isValidCoordinates(currentCoords)) {
      return; // Skip devices with invalid coordinates
    }
    
    const previousState = previousStates[deviceId];
    const previousCoords = previousState?.coordinates;
    
    if (previousCoords && isValidCoordinates(previousCoords)) {
      // Check for geofence transitions
      const transitions = checkGeofenceTransitions(previousCoords, currentCoords, geofences);
      results[deviceId] = {
        device,
        coordinates: currentCoords,
        transitions,
        timestamp: new Date().toISOString()
      };
    } else {
      // First time seeing this device, check initial geofence status
      const containingGeofences = getContainingGeofences(currentCoords, geofences);
      results[deviceId] = {
        device,
        coordinates: currentCoords,
        transitions: { entered: containingGeofences, exited: [], stayed: [] },
        timestamp: new Date().toISOString()
      };
    }
  });
  
  return results;
};
